import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { api, CATEGORY_META } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ShieldCheck, Zap, Wrench, MessageSquareCode, BookText, ArrowRight,
  Mail, KeyRound, Cpu, Activity, TerminalSquare, History, HeartPulse,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const CATS = [
  { name: "Windows OS", desc: "BSOD, updates, drivers, disk & memory health", icon: Cpu },
  { name: "MS Office Apps", desc: "Outlook, Teams, Word, 365 licensing", icon: Mail },
  { name: "MS Account / Activation", desc: "Activation errors, Azure AD, Windows Hello", icon: KeyRound },
];

const TOOLS = [
  { to: "/scanner", title: "System Scanner", desc: "Run a simulated deep scan and get a live health score.", icon: ShieldCheck, cta: "Run scan" },
  { to: "/assistant", title: "AI Assistant", desc: "Describe your problem in plain words, get an instant fix.", icon: MessageSquareCode, cta: "Ask AI" },
  { to: "/troubleshooter", title: "Guided Troubleshooter", desc: "Answer a few questions for a targeted resolution.", icon: Wrench, cta: "Start" },
  { to: "/knowledge-base", title: "Knowledge Base", desc: "Browse common Microsoft issues and one-click fixes.", icon: BookText, cta: "Browse" },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!user) { setHistory([]); return; }
    api.get("/my-scans").then((r) => setHistory(r.data)).catch(() => {});
  }, [user]);

  const scoreColor = (s) => (s >= 80 ? "#10B981" : s >= 55 ? "#F59E0B" : "#EF4444");

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border/60 mt-8 grid-texture">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/40" />
        <div className="relative px-6 sm:px-12 py-16 sm:py-24 max-w-3xl">
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 mb-6">
            <Activity className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">Microsoft Ecosystem Diagnostics</span>
          </motion.div>

          <motion.h1 initial="hidden" animate="show" variants={fadeUp} custom={1}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none">
            Detect & fix Microsoft issues
            <span className="block text-primary">on any laptop</span>
          </motion.h1>

          <motion.p initial="hidden" animate="show" variants={fadeUp} custom={2}
            className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            OmniFix AI scans for Windows, Office and account problems, then hands you clear fixes —
            plain-English for everyday users, PowerShell scripts for IT pros.
          </motion.p>

          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={3}
            className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" onClick={() => navigate("/scanner")}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl h-12 px-6"
              data-testid="hero-scan-btn">
              <Zap className="h-5 w-5 mr-2" /> Run Quick Scan
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/assistant")}
              className="border-border/70 rounded-xl h-12 px-6" data-testid="hero-ai-btn">
              <TerminalSquare className="h-5 w-5 mr-2" /> Ask the AI Assistant
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="mt-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">What we detect</h2>
        <p className="text-muted-foreground mt-2">Three problem domains across the Microsoft stack.</p>
        <div className="grid sm:grid-cols-3 gap-5 mt-7">
          {CATS.map((c, i) => {
            const meta = CATEGORY_META[c.name];
            return (
              <motion.div key={c.name} initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={fadeUp} custom={i}
                className="glass rounded-2xl p-6 hover:-translate-y-0.5 transition-all duration-200"
                data-testid={`cat-card-${meta.short.toLowerCase()}`}>
                <div className="h-11 w-11 rounded-xl grid place-items-center mb-4"
                  style={{ background: `${meta.color}18`, border: `1px solid ${meta.color}40` }}>
                  <c.icon className="h-5 w-5" style={{ color: meta.color }} />
                </div>
                <h3 className="font-heading text-lg font-semibold">{c.name}</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{c.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Scan history (signed-in) */}
      {user && (
        <section className="mt-16" data-testid="scan-history-section">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Your scan history</h2>
          </div>
          <p className="text-muted-foreground mt-2">Signed in as {user.name} — your past scans are saved here.</p>
          {history.length === 0 ? (
            <div className="glass rounded-2xl p-8 mt-6 text-center text-muted-foreground" data-testid="history-empty">
              No scans yet. <button onClick={() => navigate("/scanner")} className="text-primary font-semibold">Run your first scan</button>.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {history.slice(0, 6).map((s) => (
                <div key={s.id} className="glass rounded-2xl p-5" data-testid={`history-item-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono">
                      <HeartPulse className="h-4 w-4" />
                      {new Date(s.created_at).toLocaleString()}
                    </div>
                    <span className="font-heading text-2xl font-extrabold" style={{ color: scoreColor(s.health_score) }}>
                      {s.health_score}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-sm">
                    <span className="text-red-400 font-semibold">{s.critical} critical</span>
                    <span className="text-amber-400 font-semibold">{s.warning} warn</span>
                    <span className="text-muted-foreground">{s.total_issues} total</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Tools */}
      <section className="mt-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Diagnostic tools</h2>
        <div className="grid sm:grid-cols-2 gap-5 mt-7">
          {TOOLS.map((t, i) => (
            <motion.button key={t.to} initial="hidden" whileInView="show" viewport={{ once: true }}
              variants={fadeUp} custom={i} onClick={() => navigate(t.to)}
              className="group glass rounded-2xl p-6 text-left hover:-translate-y-0.5 hover:border-primary/40 transition-all duration-200"
              data-testid={`tool-card-${t.to.replace("/", "")}`}>
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-xl bg-primary/12 border border-primary/25 grid place-items-center">
                  <t.icon className="h-5 w-5 text-primary" />
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="font-heading text-lg font-semibold mt-4">{t.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{t.desc}</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-4">{t.cta}</span>
            </motion.button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
